import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import DataTableMUI from "../../components/common/DataTable";
import KYCDocumentsForm from "./form";
import { photoGalleryColumns } from "../../services/tableColomn";
import ViewDataPopup from "./viewDataPopup";
import {
  getPhotosByCategory,
  deletePhoto,
  undoDeletePhoto,
} from "../../redux/slices/photoGallerySlice";

const statusOptions = [
  { value: "all", label: "ALL", color: "#2563eb" },
  { value: "active", label: "ACTIVE", color: "#388e3c" },
  { value: "inactive", label: "INACTIVE", color: "#bfa100" },
  { value: "deleted", label: "DELETED", color: "#d32f2f" },
];

const PhotoGallery = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { photos, loading, statusCounts, pagination } = useAppSelector(
    (state) => state.photoGallery
  );

  console.log("statusCounts:", statusCounts);
  console.log("pagination:", pagination);

  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [viewPopupOpen, setViewPopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortKey, setSortKey] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const refetch = () => {
    const searchParams = new URLSearchParams(location.search);
    const categoryId = searchParams.get("categoryId");
    const categoryType = searchParams.get("categoryType");

    if (categoryId && categoryType) {
      dispatch(
        getPhotosByCategory({
          categoryId,
          categoryType,
          page,
          limit: itemsPerPage,
          status: statusFilter,
          search,
          sortKey,
          sortOrder,
        })
      );
    }
  };

  useEffect(() => {
    refetch();
  }, [
    dispatch,
    location.search,
    page,
    itemsPerPage,
    statusFilter,
    search,
    sortKey,
    sortOrder,
  ]);

  const handleStatusChange = (status) => {
    setStatusFilter(status);
    setPage(1);
  };

  const handlePageChange = (newPage) => setPage(newPage);

  const handleView = (row) => {
    setSelectedRow(row);
    setViewPopupOpen(true);
  };

  const handleEdit = (row) => {
    setEditData(row);
    setShowForm(true);
  };

  const handleDelete = async (row) => {
    if (window.confirm("Are you sure you want to delete this photo?")) {
      try {
        await dispatch(deletePhoto(row._id));
        refetch(); // Refetch to update status counts
      } catch (error) {
        console.error("Delete failed:", error);
      }
    }
  };

  const handleUndo = async (row) => {
    try {
      await dispatch(undoDeletePhoto(row._id));
      refetch(); // Refetch to update status counts
    } catch (error) {
      console.error("Undo delete failed:", error);
    }
  };

  const handleFormClose = (refresh = false) => {
    setShowForm(false);
    setEditData(null);

    if (refresh) {
      refetch(); // Refetch to update status counts
    }
  };

  const handleSort = (col) => {
    if (!col.sortable) return;
    setSortKey(col.accessor);
    setSortOrder(
      sortKey === col.accessor && sortOrder === "asc" ? "desc" : "asc"
    );
    setPage(1);
  };

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  // Add counts to statusOptions
  const statusOpts = statusOptions.map((opt) => ({
    ...opt,
    count: statusCounts?.[opt.value] || 0,
  }));

  // Ensure photos is always an array
  const safePhotos = Array.isArray(photos) ? photos : [];

  return (
    <div>
      {showForm ? (
        <KYCDocumentsForm onClose={handleFormClose} editData={editData} />
      ) : (
        <div className="data-table">
          <DataTableMUI
            title="Photo Gallery"
            columns={photoGalleryColumns}
            data={safePhotos}
            statusOptions={statusOpts}
            statusCounts={statusCounts}
            statusFilter={statusFilter}
            onStatusFilterChange={handleStatusChange}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onUndo={handleUndo}
            pagination
            itemsPerPage={itemsPerPage}
            currentPage={pagination?.page || 1}
            totalItems={pagination?.total || 0}
            setItemsPerPage={setItemsPerPage}
            onPageChange={handlePageChange}
            searchable
            searchTerm={searchInput}
            setSearchTerm={setSearchInput}
            onSearch={handleSearch}
            onAdd
            onAddClick={() => setShowForm(true)}
            loading={loading}
            sortKey={sortKey}
            sortOrder={sortOrder}
            onSort={handleSort}
            draggable={true}
          />
          {selectedRow && (
            <ViewDataPopup
              open={viewPopupOpen}
              onClose={() => {
                setViewPopupOpen(false);
                setSelectedRow(null);
              }}
              data={selectedRow}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default PhotoGallery;
